import { defineStore } from "pinia";
import tableConfigsApi from "@/services/api/tableConfigsApi";
import { createColumnsFromServerConfig } from "@/utils/tableFormatter";
import { getCompanyId } from "@/utils/userUtils";
import attributesApi from "@/services/api/attributesApi";

export const useTableConfigStore = defineStore("tableConfig", {
  state: () => ({
    configs: [],
    activeConfig: null,
    currentFields: [],
    isLoading: false,
    apiAvailable: true,
    availableAttributesCache: new Map(),
  }),

  actions: {
    async initTableConfiguration(
      resourceModel,
      tableName,
      sortFields = []
    ) {
      this.isLoading = true;
      const companyId = getCompanyId();

      try {
        if (this.apiAvailable) {
          try {
            const response = await tableConfigsApi.index(companyId, {
              resource_model: resourceModel,
            });
            this.configs = response.data.result || [];

            // ОБНОВЛЕНО: Ищем активный конфиг по полю current
            const active =
              this.configs.find((c) => c.current) ||
              this.configs.find((c) => c.enabled) ||
              this.configs.find((c) => c.default) ||
              this.configs[0];

            this.activeConfig = active;

            if (active) {
              const fieldsResponse = await tableConfigsApi.getFields(
                companyId,
                active.id
              );
              const serverFields = fieldsResponse.data.result || [];

              const tableColumns =
                await createColumnsFromServerConfig(
                  tableName,
                  serverFields,
                  sortFields
                );

              this.currentFields = tableColumns;
              return tableColumns;
            }
          } catch (apiError) {
            console.error("API настроек недоступен:", apiError);
            if (
              apiError.response &&
              apiError.response.status >= 500
            ) {
              this.apiAvailable = false;
            }
          }
        }

        this.currentFields = [];
        return [];
      } catch (error) {
        console.error("Ошибка инициализации таблицы:", error);
        this.currentFields = [];
        return [];
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Универсальное обогащение данных для custom полей (key с точками)
     * @param {Array} records - сырые данные из API
     * @param {Array} columns - tableColumns с field.key
     * @returns {Array} обогащённые records
     */
    enrichRecordsWithCustomFields(records, columns) {
      if (!records?.length || !columns?.length) return records;

      const customFieldKeys = columns
        .filter((col) => col.key?.includes("."))
        .map((col) => col.key);

      if (customFieldKeys.length === 0) return records;

      return records.map((record) => {
        const enriched = { ...record };

        customFieldKeys.forEach((key) => {
          const path = key.split(".");
          let value = record;

          for (const part of path) {
            if (value == null) break;
            value = value[part];
          }

          enriched[key] = value ?? null;
        });

        return enriched;
      });
    },

    async saveConfiguration(companyId, configData, fieldsData) {
      if (!this.apiAvailable) throw new Error("API недоступен");

      try {
        let configId = configData.id;

        if (configId) {
          await tableConfigsApi.update(companyId, configId, {
            name: configData.name,
            default: configData.default,
          });
        } else {
          const res = await tableConfigsApi.create(
            companyId,
            configData
          );
          configId = res.data.result.id;
        }

        const fieldsToSave = fieldsData.filter(
          (field) => field.enabled !== false
        );

        const updatePromises = fieldsToSave.map((field, index) => {
          const payload = {
            field_key: field.key,
            label: field.label,
            position: index,
            enabled: field.enabled,
            width: parseInt(field.width) || 150,
          };

          if (field.id) {
            return tableConfigsApi.updateField(
              companyId,
              configId,
              field.id,
              payload
            );
          } else {
            return tableConfigsApi.createField(
              companyId,
              configId,
              payload
            );
          }
        });

        await Promise.all(updatePromises);

        if (
          !configData.id ||
          (this.activeConfig && this.activeConfig.id === configId)
        ) {
          await tableConfigsApi.enable(companyId, configId);
        }

        return configId;
      } catch (error) {
        console.error("Ошибка сохранения:", error);
        throw error;
      }
    },

    async getAvailableAttributes(companyId, recordType) {
      const cacheKey = `${companyId}_${recordType}`;

      if (this.availableAttributesCache.has(cacheKey)) {
        return this.availableAttributesCache.get(cacheKey);
      }

      try {
        const response = await attributesApi.renderAttributes(
          companyId,
          { record_type: recordType }
        );

        const parsed = response.data.parsed_attrs || [];

        const attributes = parsed.map((attr) => ({
          key: attr.path,
          label: attr.label,
          example: attr.value,
        }));

        this.availableAttributesCache.set(cacheKey, attributes);

        return attributes;
      } catch (error) {
        console.error("Ошибка загрузки атрибутов:", error);
        return [];
      }
    },

    clearAttributesCache(companyId = null, recordType = null) {
      if (companyId && recordType) {
        const cacheKey = `${companyId}_${recordType}`;
        this.availableAttributesCache.delete(cacheKey);
      } else {
        this.availableAttributesCache.clear();
      }
    },
  },
});
